export const en = {
  common: {
    cancel: 'Cancel', confirm: 'Confirm', close: 'Close', save: 'Save', done: 'Done',
    back: 'Back', retry: 'Try again', loading: 'Loading', all: 'All', none: 'None',
    somethingWrong: 'Something went wrong.', hours: 'hrs', search: 'Search',
  },
  nav: {
    vetting: 'Vetting', disputes: 'Disputes', payouts: 'Payouts',
    orgs: 'Companies', rates: 'Rates', courses: 'Courses', metrics: 'Metrics', audit: 'Audit',
  },
  auth: {
    title: 'Pinch Crew Ops',
    subtitle: 'Internal console. Every action here is recorded.',
    email: 'Work email', password: 'Password', totp: 'Authenticator code',
    signIn: 'Sign in',
    demoHint: 'Demo mode — any email, password is ops1234',
  },
  vetting: {
    title: 'Vetting queue',
    subtitle: 'Oldest first. A worker only becomes claimable once every required stage passes.',
    empty: 'Queue is clear',
    emptyBody: 'Nobody is waiting for review.',
    waiting: 'Waiting {n}h',
    docsPending: '{n} document to review | {n} documents to review',
    blockedOn: 'Blocked on {stage}',
    filterStage: 'Stage',
    filterAging: 'Waiting over 48h',
    review: 'Review',
    stages: 'Stages',
    documents: 'Documents',
    history: 'Shift history',
    interviews: 'Interviews',
    screening: 'AI screening',
    screeningHint: 'A suggestion for you to act on or ignore. Every worker starts at T1 — promote on evidence.',
    screeningSuggests: 'Suggests T{tier}',
    screeningConfidence: '{n}% confidence',
    screeningFlags: 'Worth checking',
    screeningPending: 'Answers submitted — screening has not run yet.',
    screeningNone: 'This worker has not answered the questionnaire.',
    screeningFailed: 'Screening could not run. Review the answers yourself.',
    screeningAnswers: 'Their answers',
    view: 'View',
    viewing: 'Reviewing document',
    viewLoading: 'Opening document…',
    viewNoFile: 'No file was attached to this document.',
    viewDownload: 'Open in new tab',
    verify: 'Verify',
    reject: 'Reject',
    rejectTitle: 'Reject this document',
    rejectBody: 'The worker sees this reason and can upload a replacement.',
    rejectReason: 'What is wrong with it?',
    passStage: 'Pass',
    failStage: 'Fail',
    failTitle: 'Fail this stage',
    failReason: 'Why?',
    stageNote: 'Note (optional)',
    activate: 'Activate worker',
    activateBody: 'They become visible to employers and can claim shifts immediately.',
    activateBlocked: 'Cannot activate yet',
    schedule: 'Schedule interview',
    scheduleAt: 'When?',
    scheduleMode: 'How?',
    mode: { video: 'Video call', in_person: 'In person', phone: 'Phone' },
    recordOutcome: 'Record outcome',
    outcome: { pending: 'Scheduled', passed: 'Passed', failed: 'Failed', no_show: 'No-show' },
    suspend: 'Suspend',
    suspendTitle: 'Suspend this worker',
    suspendBody: 'They cannot claim shifts while suspended. Employers are not told the reason.',
    suspendDays: 'For how many days?',
    suspendReason: 'Reason',
    waive: 'Lift suspension',
    waiveReason: 'Why are you lifting it?',
    noShows: '{n} no-show | {n} no-shows',
    shiftsCompleted: '{n} shift completed | {n} shifts completed',
    paidTotal: 'Paid to date',
    status: {
      draft: 'Draft', pending_vetting: 'In vetting', active: 'Active',
      suspended: 'Suspended', banned: 'Banned',
    },
    stageStatus: {
      not_started: 'Not started', submitted: 'Submitted', in_review: 'In review',
      passed: 'Passed', failed: 'Failed',
    },
    docStatus: { pending: 'Pending', verified: 'Verified', rejected: 'Rejected' },
    docType: {
      ic: 'MyKad (IC)', bank: 'Bank account', student_pass: 'Student pass',
      work_permit: 'Work permit', food_handling_cert: 'Food handling certificate',
      typhoid_jab: 'Typhoid vaccination',
    },
    required: 'Required', optional: 'Optional',
  },
  disputes: {
    title: 'Disputes',
    subtitle: 'Both sides have given their account. Decide the hours and record why.',
    empty: 'No open disputes',
    emptyBody: 'Nothing needs adjudicating right now.',
    raisedByWorker: 'Raised by the worker',
    raisedByOrg: 'Raised by the company',
    recorded: 'Clock recorded',
    claimed: 'They say it should be',
    resolve: 'Resolve',
    resolveTitle: 'Resolve this dispute',
    outcomeLabel: 'Who is right?',
    outcomeWorker: 'Worker — pay the claimed hours',
    outcomeOrg: 'Company — keep the recorded hours',
    outcomeSplit: 'Split — pay something in between',
    paidMinutes: 'Hours to pay',
    note: 'Why did you decide this?',
    noteHint: 'The worker and the company both see this.',
    status: {
      open: 'Open', investigating: 'Investigating',
      resolved_worker: 'Found for worker', resolved_org: 'Found for company',
      resolved_split: 'Split',
    },
  },
  payouts: {
    title: 'Payout runs',
    subtitle: 'A run is prepared by one person and released by another.',
    prepare: 'Prepare a run',
    prepareTitle: 'Prepare a payout run',
    periodStart: 'From', periodEnd: 'To',
    empty: 'No payout runs yet',
    emptyBody: 'Prepare one to pay this period.',
    workers: '{n} worker | {n} workers',
    totalNet: 'Total',
    preparedBy: 'Prepared by {name}',
    releasedBy: 'Released by {name}',
    release: 'Release payment',
    releaseTitle: 'Release this run?',
    releaseBody: 'Money leaves the platform account and reaches workers. This cannot be undone.',
    blockedLines: '{n} payout blocked | {n} payouts blocked',
    blockedBody: 'Clear every blocker before releasing.',
    fourEyes: 'You prepared this run',
    fourEyesBody: 'Someone else has to release it. That is deliberate.',
    status: {
      draft: 'Draft', awaiting_release: 'Awaiting release',
      released: 'Released', failed: 'Failed',
    },
  },
  orgs: {
    title: 'Companies',
    add: 'Add a company',
    addTitle: 'Add a company',
    displayName: 'Trading name', legalName: 'Registered name',
    billingEmail: 'Billing email', feeRate: 'Platform fee (%)',
    paymentMode: 'How do they pay?',
    paymentModes: { per_booking: 'Card, per booking', wallet: 'Prepaid wallet' },
    sites: '{n} site | {n} sites',
    workers: '{n} worker | {n} workers',
    posted: '{n} shift posted | {n} shifts posted',
    balance: 'Balance',
    suspend: 'Suspend',
    suspendReason: 'Why?',
    status: { active: 'Active', suspended: 'Suspended', onboarding: 'Onboarding' },
    empty: 'No companies yet',
  },
  rates: {
    title: 'Rates',
    subtitle: 'Floors are the platform minimum. Cards are what a worker is actually paid.',
    floors: 'Platform floors',
    cards: 'Rate cards',
    global: 'All companies',
    addCard: 'Add a rate card',
    editCard: 'Edit rate card',
    skill: 'Role', tier: 'Tier', payRate: 'Worker pay (RM/hour)',
    billRate: 'Bill rate (RM/hour, optional)',
    forOrg: 'For which company?',
    effectiveFrom: 'Effective from',
    setFloor: 'Set floor',
    minPay: 'Minimum pay (RM/hour)',
    floorNote: 'A card below its floor is refused.',
  },
  courses: {
    title: 'Courses',
    subtitle: 'Passing a course raises a tier, which raises pay. Every published course needs a quiz.',
    add: 'New course',
    edit: 'Edit',
    publish: 'Publish',
    published: 'Published',
    draft: 'Draft',
    courseTitle: 'Title', summary: 'Summary', skill: 'Raises which skill?',
    grantsTier: 'Grants tier', duration: 'Duration (minutes)', uplift: 'Rate uplift (RM/hour)',
    modules: 'Modules', addModule: 'Add a module',
    moduleTitle: 'Module title', moduleType: 'Type', moduleBody: 'Content',
    moduleDuration: 'Minutes',
    contentType: { article: 'Article', video: 'Video', quiz: 'Quiz', in_person: 'In person' },
    questions: 'Quiz questions', addQuestion: 'Add a question',
    questionText: 'Question', choices: 'Choices', correct: 'Correct answer',
    publishBlocked: 'Cannot publish yet',
    empty: 'No courses yet',
  },
  metrics: {
    title: 'Platform health',
    fillRate: 'Fill rate',
    fillRateHint: 'Slots filled out of slots posted',
    timeToFill: 'Median time to fill',
    noShowRate: 'No-show rate',
    repeatRate: 'Repeat rate',
    repeatRateHint: 'Workers returning to the same site',
    activeWorkers: 'Active workers',
    activeOrgs: 'Active companies',
    shiftsPosted: 'Shifts posted',
    hoursWorked: 'Hours worked',
    grossVolume: 'Gross volume',
    revenue: 'Platform revenue',
    trend: 'Fill rate by week',
    period: { '7d': '7 days', '30d': '30 days', '90d': '90 days' },
  },
  audit: {
    title: 'Audit trail',
    subtitle: 'Every action taken in this console, newest first.',
    empty: 'Nothing recorded yet',
    action: {
      'vetting.stage_passed': 'passed a stage for',
      'vetting.stage_failed': 'failed a stage for',
      'vetting.document_verified': 'verified a document for',
      'vetting.document_rejected': 'rejected a document for',
      'vetting.interview_scheduled': 'scheduled an interview with',
      'vetting.worker_activated': 'activated',
      'worker.suspended': 'suspended',
      'worker.suspension_waived': 'lifted the suspension on',
      'dispute.resolved': 'resolved a dispute for',
      'payout.prepared': 'prepared a payout run for',
      'payout.released': 'released a payout run for',
      'org.created': 'created',
      'org.updated': 'updated',
      'org.suspended': 'suspended',
      'rate.created': 'created a rate card for',
      'rate.updated': 'updated a rate card for',
      'course.created': 'created the course',
      'course.updated': 'updated the course',
      'course.published': 'published the course',
    },
  },
  roles: { reviewer: 'Reviewer', admin: 'Admin', finance: 'Finance', support: 'Support' },
  forbidden: 'Your role does not allow that.',
  signOut: 'Sign out',
} as const
